/**
 * @file api.ts
 * @brief React-Query hook for the AI provider→model switchboard catalog.
 *
 * @details
 * useAiCatalog → GET /api/admin/ai/catalog — per stage (TTS/ASR) every known
 * provider with its selectable models and a `configured` flag (whether its
 * API keys exist on the backend). Dormant providers (configured:false, e.g.
 * google/azure until their keys are added) are shown disabled in the pickers.
 * Credential values are never part of this payload.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/{client,endpoints}
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (GET /ai/catalog)
 * - BACKEND/src/infrastructure/ai/catalog.mjs (source of truth)
 * - WEBPANEL/src/features/settings/components/AiConfigurationCard.tsx (consumer)
 *
 * @author DES
 * @date 11-07-2026
 */

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import { dropRetiredModels } from "./components/aiPipeline.ts";

const CatalogModelSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const CatalogProviderSchema = z.object({
  label: z.string(),
  models: z.array(CatalogModelSchema),
  universal: z.boolean().optional().default(false),
  languages: z.array(z.string()).nullable().optional(),
  configured: z.boolean(),
  // Azure only (16-10-2026): voices that speak Urdu, for the Interpreter's
  // Urdu male / female voice pickers (BACKEND catalog AZURE_URDU_VOICES).
  urduVoices: z
    .object({ male: z.array(CatalogModelSchema), female: z.array(CatalogModelSchema) })
    .optional(),
});

const AiCatalogResponseSchema = z.object({
  success: z.boolean().optional(),
  catalog: z.object({
    TTS: z.record(z.string(), CatalogProviderSchema),
    ASR: z.record(z.string(), CatalogProviderSchema),
    // Phase-2 (11-07-2026): Boardroom synthesis + Notes insights vendors.
    LLM: z.record(z.string(), CatalogProviderSchema),
  }),
});

export type AiCatalogProvider = z.infer<typeof CatalogProviderSchema>;
export type AiCatalog = z.infer<typeof AiCatalogResponseSchema>["catalog"];

export const settingsKeys = {
  all: ["admin", "settings-feature"] as const,
  aiCatalog: () => [...settingsKeys.all, "ai-catalog"] as const,
};

/**
 * The switchboard catalog changes only when the backend deploys or an env
 * key is added (a restart), so a long staleTime avoids refetch churn while
 * the admin plays with the pickers.
 */
export function useAiCatalog(options?: Partial<UseQueryOptions<AiCatalog>>) {
  return useQuery<AiCatalog>({
    queryKey: settingsKeys.aiCatalog(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.aiCatalog);
      // W15: never offer a model OpenAI retired (the backend still lists gpt-4.1-nano).
      return dropRetiredModels(AiCatalogResponseSchema.parse(raw).catalog);
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}
